import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Grid3X3, Plus, Trash2, Save, Settings, DollarSign, 
  Monitor, Box, Layers, Edit2, X, Check, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { storesAPI, revenueAPI } from '@/services/api';

// Cell type colors for visualization
const CELL_TYPE_COLORS = {
  pro_premium: { bg: 'bg-purple-500', text: 'text-white', label: 'Pro Premium', border: 'border-purple-600' },
  premium: { bg: 'bg-blue-500', text: 'text-white', label: 'Premium', border: 'border-blue-600' },
  standard: { bg: 'bg-gray-400', text: 'text-white', label: 'Standard', border: 'border-gray-500' },
  free: { bg: 'bg-green-500', text: 'text-white', label: 'Free', border: 'border-green-600' }
};

const StoreRackSettingsPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);
  const [activeTab, setActiveTab] = useState('racks');
  
  // Rack Settings State
  const [rackSettings, setRackSettings] = useState({ racks: [] });
  const [rackDialogOpen, setRackDialogOpen] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [deleteRackDialog, setDeleteRackDialog] = useState(false);
  const [rackToDelete, setRackToDelete] = useState(null);
  
  // New rack form
  const [rackForm, setRackForm] = useState({
    rack_id: '',
    rack_name: '',
    columns: 3,
    rows: 4
  });
  
  // Cell editing
  const [selectedCell, setSelectedCell] = useState(null);
  
  // Revenue Settings State
  const [revenueSettings, setRevenueSettings] = useState({
    product_registration_fee: 0,
    product_registration_tax_percent: 18,
    cell_rental_rates: {
      pro_premium: 0,
      premium: 0,
      standard: 0,
      free: 0
    },
    cell_rental_tax_percent: 18,
    display_ad_rate: 0,
    display_ad_tax_percent: 18
  });

  // Fetch store details
  const fetchStore = useCallback(async () => {
    try {
      const response = await storesAPI.get(storeId);
      setStore(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch store details', variant: 'destructive' });
      navigate('/hq/stores');
    }
  }, [storeId, toast, navigate]);

  // Fetch rack settings
  const fetchRackSettings = useCallback(async () => {
    try {
      const response = await revenueAPI.getStoreRackSettings(storeId);
      setRackSettings(response.data || { racks: [] });
    } catch (error) {
      console.error('Failed to fetch rack settings:', error);
    }
  }, [storeId]);

  // Fetch revenue settings
  const fetchRevenueSettings = useCallback(async () => {
    try {
      const response = await revenueAPI.getStoreRevenueSettings(storeId);
      if (response.data) {
        setRevenueSettings({
          product_registration_fee: response.data.product_registration_fee || 0,
          product_registration_tax_percent: response.data.product_registration_tax_percent || 18,
          cell_rental_rates: response.data.cell_rental_rates || {
            pro_premium: 0, premium: 0, standard: 0, free: 0
          },
          cell_rental_tax_percent: response.data.cell_rental_tax_percent || 18,
          display_ad_rate: response.data.display_ad_rate || 0,
          display_ad_tax_percent: response.data.display_ad_tax_percent || 18
        });
      }
    } catch (error) {
      console.error('Failed to fetch revenue settings:', error);
    }
  }, [storeId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStore(), fetchRackSettings(), fetchRevenueSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStore, fetchRackSettings, fetchRevenueSettings]);

  // Generate column labels (A, B, C, ...)
  const generateColumnLabels = (count) => {
    return Array.from({ length: count }, (_, i) => String.fromCharCode(65 + i));
  };

  // Generate row numbers
  const generateRowNumbers = (count) => {
    return Array.from({ length: count }, (_, i) => i + 1);
  };

  // Create cells for a rack
  const createCellsForRack = (rackId, columns, rows) => {
    const cells = [];
    const columnLabels = generateColumnLabels(columns);
    const rowNumbers = generateRowNumbers(rows);
    
    for (const col of columnLabels) {
      for (const row of rowNumbers) {
        cells.push({
          cell_id: `${rackId}-${col}-${row}`,
          type: 'standard'
        });
      }
    }
    return cells;
  };

  // Create column displays for a rack
  const createColumnDisplays = (columns) => {
    const columnLabels = generateColumnLabels(columns);
    return columnLabels.map(col => ({
      column: col,
      has_display: false
    }));
  };

  // Handle add/edit rack
  const handleOpenRackDialog = (rack = null) => {
    if (rack) {
      setEditingRack(rack);
      setRackForm({
        rack_id: rack.rack_id,
        rack_name: rack.rack_name || '',
        columns: rack.columns?.length || 3,
        rows: rack.rows?.length || 4
      });
    } else {
      setEditingRack(null);
      // Auto-generate next rack ID
      const existingIds = rackSettings.racks.map(r => r.rack_id);
      let nextId = 1;
      while (existingIds.includes(`R${nextId}`)) nextId++;
      setRackForm({
        rack_id: `R${nextId}`,
        rack_name: '',
        columns: 3,
        rows: 4
      });
    }
    setRackDialogOpen(true);
  };

  const handleSaveRack = () => {
    if (!rackForm.rack_id) {
      toast({ title: 'Error', description: 'Rack ID is required', variant: 'destructive' });
      return;
    }

    const columns = generateColumnLabels(rackForm.columns);
    const rows = generateRowNumbers(rackForm.rows);
    
    if (editingRack) {
      // Update existing rack
      const updatedRacks = rackSettings.racks.map(r => {
        if (r.rack_id === editingRack.rack_id) {
          // Preserve existing cells where possible
          const newCells = createCellsForRack(rackForm.rack_id, rackForm.columns, rackForm.rows);
          const existingCellMap = {};
          r.cells?.forEach(c => { existingCellMap[c.cell_id] = c; });
          
          const mergedCells = newCells.map(cell => {
            const existing = existingCellMap[cell.cell_id];
            return existing || cell;
          });

          // Preserve existing display settings
          const newDisplays = createColumnDisplays(rackForm.columns);
          const existingDisplayMap = {};
          r.column_displays?.forEach(d => { existingDisplayMap[d.column] = d; });
          
          const mergedDisplays = newDisplays.map(disp => {
            const existing = existingDisplayMap[disp.column];
            return existing || disp;
          });

          return {
            ...r,
            rack_id: rackForm.rack_id,
            rack_name: rackForm.rack_name,
            columns: columns,
            rows: rows,
            cells: mergedCells,
            column_displays: mergedDisplays
          };
        }
        return r;
      });
      setRackSettings({ ...rackSettings, racks: updatedRacks });
    } else {
      // Check for duplicate ID
      if (rackSettings.racks.some(r => r.rack_id === rackForm.rack_id)) {
        toast({ title: 'Error', description: 'Rack ID already exists', variant: 'destructive' });
        return;
      }

      const newRack = {
        rack_id: rackForm.rack_id,
        rack_name: rackForm.rack_name,
        columns: columns,
        rows: rows,
        cells: createCellsForRack(rackForm.rack_id, rackForm.columns, rackForm.rows),
        column_displays: createColumnDisplays(rackForm.columns)
      };
      setRackSettings({ ...rackSettings, racks: [...rackSettings.racks, newRack] });
    }
    
    setRackDialogOpen(false);
    toast({ title: 'Success', description: editingRack ? 'Rack updated' : 'Rack added' });
  };

  const handleDeleteRack = () => {
    if (rackToDelete) {
      setRackSettings({
        ...rackSettings,
        racks: rackSettings.racks.filter(r => r.rack_id !== rackToDelete.rack_id)
      });
      setDeleteRackDialog(false);
      setRackToDelete(null);
      toast({ title: 'Success', description: 'Rack deleted' });
    }
  };

  // Update cell type
  const updateCellType = (rackId, cellId, newType) => {
    const updatedRacks = rackSettings.racks.map(rack => {
      if (rack.rack_id === rackId) {
        return {
          ...rack,
          cells: rack.cells.map(cell => 
            cell.cell_id === cellId ? { ...cell, type: newType } : cell
          )
        };
      }
      return rack;
    });
    setRackSettings({ ...rackSettings, racks: updatedRacks });
  };

  // Toggle column display
  const toggleColumnDisplay = (rackId, column) => {
    const updatedRacks = rackSettings.racks.map(rack => {
      if (rack.rack_id === rackId) {
        return {
          ...rack,
          column_displays: rack.column_displays.map(disp => 
            disp.column === column ? { ...disp, has_display: !disp.has_display } : disp
          )
        };
      }
      return rack;
    });
    setRackSettings({ ...rackSettings, racks: updatedRacks });
  };

  // Save all settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Save rack settings
      await revenueAPI.updateStoreRackSettings(storeId, { racks: rackSettings.racks });
      
      // Save revenue settings
      await revenueAPI.updateStoreRevenueSettings(storeId, revenueSettings);
      
      toast({ title: 'Success', description: 'Settings saved successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save settings', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Get cell from rack
  const getCellFromRack = (rack, col, row) => {
    const cellId = `${rack.rack_id}-${col}-${row}`;
    return rack.cells?.find(c => c.cell_id === cellId) || { cell_id: cellId, type: 'standard' };
  };

  // Get display status for column
  const getColumnDisplay = (rack, col) => {
    return rack.column_displays?.find(d => d.column === col) || { column: col, has_display: false };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="store-rack-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/hq/stores')} className="text-gray-600">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Rack & Revenue Settings</h1>
            <p className="text-gray-500 text-sm">
              {store?.name} ({store?.code})
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSaveSettings} 
          disabled={saving} 
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-all-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="racks" className="data-[state=active]:bg-white">
            <Grid3X3 className="w-4 h-4 mr-2" /> Rack Layout
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-white">
            <DollarSign className="w-4 h-4 mr-2" /> Revenue Settings
          </TabsTrigger>
        </TabsList>

        {/* Rack Layout Tab */}
        <TabsContent value="racks" className="mt-6 space-y-6">
          {/* Legend */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-sm text-gray-600 font-medium">Cell Types:</span>
                {Object.entries(CELL_TYPE_COLORS).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${value.bg}`}></div>
                    <span className="text-sm text-gray-600">{value.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
                  <Monitor className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-gray-600">= Ad Display</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add Rack Button */}
          <div className="flex justify-end">
            <Button onClick={() => handleOpenRackDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-rack-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Rack
            </Button>
          </div>

          {/* Racks Grid */}
          {rackSettings.racks.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="py-12 text-center">
                <Grid3X3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No racks configured</p>
                <p className="text-gray-400 text-sm mb-4">Add your first rack to start managing shelf spaces</p>
                <Button onClick={() => handleOpenRackDialog()} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Add First Rack
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {rackSettings.racks.map((rack) => (
                <Card key={rack.rack_id} className="bg-white border-gray-200" data-testid={`rack-${rack.rack_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                          <Layers className="w-5 h-5 text-indigo-500" />
                          {rack.rack_id}
                          {rack.rack_name && (
                            <span className="text-gray-500 font-normal text-sm">- {rack.rack_name}</span>
                          )}
                        </CardTitle>
                        <CardDescription>
                          {rack.columns?.length || 0} columns × {rack.rows?.length || 0} rows
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenRackDialog(rack)}>
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => { setRackToDelete(rack); setDeleteRackDialog(true); }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Column Display Row */}
                    <div className="mb-2">
                      <div className="flex gap-1 ml-8">
                        {(rack.columns || []).map((col) => {
                          const display = getColumnDisplay(rack, col);
                          return (
                            <div 
                              key={col} 
                              className="flex-1 flex justify-center"
                              title="Click to toggle display"
                            >
                              <button
                                onClick={() => toggleColumnDisplay(rack.rack_id, col)}
                                className={`p-1 rounded transition-colors ${
                                  display.has_display 
                                    ? 'bg-amber-100 text-amber-600' 
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                              >
                                <Monitor className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Column Headers */}
                    <div className="flex gap-1 mb-1 ml-8">
                      {(rack.columns || []).map((col) => (
                        <div key={col} className="flex-1 text-center text-xs font-semibold text-gray-500">
                          {col}
                        </div>
                      ))}
                    </div>

                    {/* Rack Grid */}
                    <div className="space-y-1">
                      {(rack.rows || []).map((row) => (
                        <div key={row} className="flex gap-1">
                          <div className="w-8 flex items-center justify-center text-xs font-semibold text-gray-500">
                            {row}
                          </div>
                          {(rack.columns || []).map((col) => {
                            const cell = getCellFromRack(rack, col, row);
                            const cellType = CELL_TYPE_COLORS[cell.type] || CELL_TYPE_COLORS.standard;
                            const isSelected = selectedCell === cell.cell_id;
                            
                            return (
                              <div
                                key={cell.cell_id}
                                className={`flex-1 h-10 rounded cursor-pointer transition-all flex items-center justify-center text-[10px] font-medium ${cellType.bg} ${cellType.text} ${
                                  isSelected ? `ring-2 ring-offset-2 ring-indigo-500 ${cellType.border}` : ''
                                }`}
                                onClick={() => setSelectedCell(isSelected ? null : cell.cell_id)}
                                title={`${cell.cell_id} - ${cellType.label}`}
                                data-testid={`cell-${cell.cell_id}`}
                              >
                                {col}{row}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {/* Cell Type Selector (when cell selected) */}
                    {selectedCell && selectedCell.startsWith(rack.rack_id) && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Cell: {selectedCell}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedCell(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          {Object.entries(CELL_TYPE_COLORS).map(([key, value]) => (
                            <button
                              key={key}
                              onClick={() => {
                                updateCellType(rack.rack_id, selectedCell, key);
                              }}
                              className={`flex-1 py-2 px-3 rounded text-xs font-medium transition-colors ${value.bg} ${value.text} hover:opacity-90`}
                            >
                              {value.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Revenue Settings Tab */}
        <TabsContent value="revenue" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Registration Fee */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Box className="w-5 h-5 text-blue-500" />
                  Product Registration Fee
                </CardTitle>
                <CardDescription>
                  One-time fee charged to manufacturers when a product is added to this store's inventory
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Fee Amount (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={revenueSettings.product_registration_fee}
                      onChange={(e) => setRevenueSettings(p => ({
                        ...p,
                        product_registration_fee: parseFloat(e.target.value) || 0
                      }))}
                      className="bg-gray-50 border-gray-300"
                      data-testid="product-reg-fee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Tax (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={revenueSettings.product_registration_tax_percent}
                      onChange={(e) => setRevenueSettings(p => ({
                        ...p,
                        product_registration_tax_percent: parseFloat(e.target.value) || 0
                      }))}
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Total:</strong> ₹{(revenueSettings.product_registration_fee * (1 + revenueSettings.product_registration_tax_percent / 100)).toFixed(2)} (incl. tax)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Display Ad Rental */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-amber-500" />
                  Display Advertisement Rental
                </CardTitle>
                <CardDescription>
                  Monthly rental for column-top advertisement display spaces
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Rate per Display (₹/month)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={revenueSettings.display_ad_rate}
                      onChange={(e) => setRevenueSettings(p => ({
                        ...p,
                        display_ad_rate: parseFloat(e.target.value) || 0
                      }))}
                      className="bg-gray-50 border-gray-300"
                      data-testid="display-ad-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Tax (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={revenueSettings.display_ad_tax_percent}
                      onChange={(e) => setRevenueSettings(p => ({
                        ...p,
                        display_ad_tax_percent: parseFloat(e.target.value) || 0
                      }))}
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Total:</strong> ₹{(revenueSettings.display_ad_rate * (1 + revenueSettings.display_ad_tax_percent / 100)).toFixed(2)} per display/month (incl. tax)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Shelf/Cell Rental Rates */}
            <Card className="bg-white border-gray-200 lg:col-span-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Grid3X3 className="w-5 h-5 text-purple-500" />
                  Shelf/Rack Cell Rental Rates
                </CardTitle>
                <CardDescription>
                  Monthly rental rates for different cell types (configurable per rack)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(CELL_TYPE_COLORS).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-gray-700 flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${value.bg}`}></div>
                        {value.label} (₹/month)
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={revenueSettings.cell_rental_rates[key]}
                        onChange={(e) => setRevenueSettings(p => ({
                          ...p,
                          cell_rental_rates: {
                            ...p.cell_rental_rates,
                            [key]: parseFloat(e.target.value) || 0
                          }
                        }))}
                        className="bg-gray-50 border-gray-300"
                        data-testid={`cell-rate-${key}-input`}
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Tax on Cell Rentals (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={revenueSettings.cell_rental_tax_percent}
                      onChange={(e) => setRevenueSettings(p => ({
                        ...p,
                        cell_rental_tax_percent: parseFloat(e.target.value) || 0
                      }))}
                      className="bg-gray-50 border-gray-300 w-32"
                    />
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">Rate Summary (incl. {revenueSettings.cell_rental_tax_percent}% tax):</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-purple-700">
                    {Object.entries(CELL_TYPE_COLORS).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium">{value.label}:</span> ₹{(revenueSettings.cell_rental_rates[key] * (1 + revenueSettings.cell_rental_tax_percent / 100)).toFixed(2)}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">How Revenue Works</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Product Registration:</strong> Charged once when a product is first added to this store</li>
                    <li>• <strong>Cell Rental:</strong> Monthly charge based on cell type where manufacturer's products are placed</li>
                    <li>• <strong>Display Ad:</strong> Monthly charge for advertisement space on rack column tops</li>
                    <li>• Invoices are auto-generated monthly on the day configured in HQ Revenue Settings</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Rack Dialog */}
      <Dialog open={rackDialogOpen} onOpenChange={setRackDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Grid3X3 className="w-5 h-5 text-indigo-600" />
              {editingRack ? 'Edit Rack' : 'Add New Rack'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Rack ID *</Label>
              <Input
                value={rackForm.rack_id}
                onChange={(e) => setRackForm(p => ({ ...p, rack_id: e.target.value.toUpperCase() }))}
                placeholder="e.g., R1"
                className="bg-gray-50 border-gray-300"
                disabled={!!editingRack}
                data-testid="rack-id-input"
              />
              <p className="text-xs text-gray-500">Unique identifier for the rack (e.g., R1, R2, MAIN)</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Rack Name</Label>
              <Input
                value={rackForm.rack_name}
                onChange={(e) => setRackForm(p => ({ ...p, rack_name: e.target.value }))}
                placeholder="e.g., Main Entrance Rack"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Columns</Label>
                <Select 
                  value={String(rackForm.columns)} 
                  onValueChange={(v) => setRackForm(p => ({ ...p, columns: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} Column{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-700">Rows</Label>
                <Select 
                  value={String(rackForm.rows)} 
                  onValueChange={(v) => setRackForm(p => ({ ...p, rows: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} Row{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                This will create <strong>{rackForm.columns * rackForm.rows}</strong> cells 
                ({rackForm.columns} × {rackForm.rows})
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRackDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button onClick={handleSaveRack} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-rack-btn">
              {editingRack ? 'Update Rack' : 'Add Rack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Rack Confirmation */}
      <AlertDialog open={deleteRackDialog} onOpenChange={setDeleteRackDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rack?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove rack "{rackToDelete?.rack_id}" and all its cell configurations. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRack} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StoreRackSettingsPage;
